// The account around the company choices (NoCompanyView.accountHeader and accountActions, DeleteAccountButton):
// who is signed in, "Use a different account" (signs this browser out; there is no company data here to lose), and
// "Delete my account" behind a typed confirmation (WEB_PLAN.md §4.7), with the delete-account call and error wording
// of the Account page. Deleting must be reachable from here (App Review 5.1.1(v) on iOS; the same account is used).

import { ArrowLeftRight, Trash, UserRoundCheck } from 'lucide-react';
import { useState } from 'react';
import { deleteAccount } from '@/auth/account';
import { signOutNow } from '@/auth/AuthProvider';
import { setSignedOutNotice } from '@/auth/pending';
import { Button, Card, ConfirmDialog, GT_COLORS, IconBadge } from '@/ui';
import { ACCOUNT_DELETED_NOTICE, DELETE_CAPTION, deleteAccountConfirmation, deleteErrorMessage } from '../welcomeText';
import { Footnote } from './Bits';

export function AccountHeader({ account }: { account: string | null }) {
  return (
    <Card padding="md" className="flex items-center gap-3">
      <IconBadge icon={<UserRoundCheck />} color={GT_COLORS.green} size={44} />
      <div className="min-w-0">
        <p className="text-xs text-fg-muted">Signed in as</p>
        <p className="truncate text-[15px] font-semibold text-fg" title={account ?? undefined}>
          {account ?? 'your account'}
        </p>
      </div>
    </Card>
  );
}

export interface AccountActionsProps {
  account: string | null;
  /** Something else is running: both buttons are off. */
  disabled: boolean;
  signingOut: boolean;
  onSignOut: () => void;
  /** Deleting started / ended (the page turns everything else off meanwhile). */
  onDeletingChange: (deleting: boolean) => void;
}

export function AccountActions({ account, disabled, signingOut, onSignOut, onDeletingChange }: AccountActionsProps) {
  const [confirming, setConfirming] = useState(false);
  const text = deleteAccountConfirmation(account);

  const remove = async () => {
    onDeletingChange(true);
    try {
      await deleteAccount();
    } catch (e) {
      onDeletingChange(false);
      // Shown in the dialog, which stays open.
      throw new Error(deleteErrorMessage(e, typeof navigator === 'undefined' || navigator.onLine !== false));
    }
    setSignedOutNotice(ACCOUNT_DELETED_NOTICE);
    // SIGNED_OUT takes the browser to the sign-in screen, which shows the notice.
    await signOutNow();
  };

  return (
    <section aria-label="Account" className="flex flex-col gap-3 sm:flex-row sm:items-start">
      <Button
        variant="secondary"
        size="lg"
        icon={<ArrowLeftRight />}
        loading={signingOut}
        disabled={disabled}
        onClick={onSignOut}
        className="w-full sm:w-auto sm:flex-1"
      >
        {signingOut ? 'Signing out…' : 'Use a different account'}
      </Button>
      <div className="flex w-full flex-col gap-1.5 sm:flex-1">
        <Button
          variant="secondary"
          size="lg"
          icon={<Trash className="text-danger-text" />}
          disabled={disabled || signingOut}
          onClick={() => setConfirming(true)}
          fullWidth
        >
          <span className="text-danger-text">Delete my account</span>
        </Button>
        <Footnote className="px-1">{DELETE_CAPTION}</Footnote>
      </div>
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={remove}
        title={text.title}
        message={text.message}
        confirmText="DELETE"
        confirmLabel={text.confirmLabel}
        destructive
      />
    </section>
  );
}
